/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Grid, Typography } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  ReactHookFormHiddenInput,
  isGeometryDirty,
} from '@opengeoweb/form-fields';
import { usePrevious } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  uiSelectors,
  genericSelectors,
  mapSelectors,
  uiTypes,
} from '@opengeoweb/store';
import type { OpenLayersLayerProps } from '@opengeoweb/webmap-react';
import {
  addFeatureProperties,
  rewindGeometry,
  useMapDrawTool,
} from '@opengeoweb/webmap-react';
import { useTACApi } from '../../../api/hooks';
import { useShortTestHelpers } from '../../../hooks/useShortTestHelpers';
import type {
  AviationPhenomenon,
  AviationProduct,
  CancelSigmet,
  FormMode,
  ProductConfig,
  Sigmet,
  SigmetConfig,
} from '../../../types';
import { StartOrEndDrawing } from '../../../types';
import { sigmetConfig as defaultSigmetConfig } from '../../../utils/config';
import { useSigmetAirmetTranslation } from '../../../utils/i18n';
import {
  featurePropsEnd,
  featurePropsFIR,
  featurePropsIntersectionEnd,
  featurePropsIntersectionStart,
  featurePropsStart,
} from '../../MapViewGeoJson/constants';
import { formStyles, styles } from '../ProductForm.styles';
import {
  Change,
  Levels,
  ObservationForecast,
  ObservationForecastTime,
  Phenomenon,
  Progress,
  SelectFIR,
  StartGeometry,
  Type,
  ValidFrom,
  ValidUntil,
  VolcanicFields,
} from '../ProductFormFields';
import IssuedAt from '../ProductFormFields/IssuedAt';
import ProductFormFieldLayout from '../ProductFormFields/ProductFormFieldLayout';
import Tac from '../ProductFormFields/Tac';
import ProductFormLayout from '../ProductFormLayout';
import { useTAC } from '../ProductFormTac';
import { useGeoJSONHandling } from '../useStoredGeoJSONHandling';
import {
  getDefaultPhenomena,
  getDrawTools,
  getFir,
  getOmitChangeFromConfig,
  getProductIssueDate,
  updateDrawToolsWithFir,
  useFormLayers,
} from '../utils';

const useConditionalFields = (
  productConfig: ProductConfig,
): {
  hasVolcanicAshes: boolean;
  omitChange: boolean;
} => {
  const { watch, unregister } = useFormContext();
  const hasVolcanicAshes = watch('phenomenon') === 'VA_CLD';
  const hasRadioactiveCloud = watch('phenomenon') === 'RDOACT_CLD';
  const changeFromConfig = getOmitChangeFromConfig(productConfig);
  const omitChange =
    changeFromConfig && (hasVolcanicAshes || hasRadioactiveCloud);
  React.useEffect(() => {
    if (!hasVolcanicAshes) {
      unregister([
        'vaSigmetVolcanoName',
        'vaSigmetVolcanoCoordinates.latitude',
        'vaSigmetVolcanoCoordinates.longitude',
      ]);
    }
    if (omitChange) {
      unregister(['change']);
    }
  }, [hasVolcanicAshes, omitChange, unregister]);
  return { hasVolcanicAshes, omitChange };
};

export interface SigmetFormProps {
  mode: FormMode;
  isCancelSigmet?: boolean;
  initialSigmet?: Sigmet;
  initialCancelSigmet?: CancelSigmet;
  showMap?: boolean;
  productConfig?: ProductConfig;
  setDialogActionsDisabled?: (disabled: boolean) => void;
  product?: AviationProduct;
  getTAC?: (product: AviationProduct) => Promise<{ data: string }>;
  storedGeoJSON?: GeoJSON.FeatureCollection;
  storedEndGeoJSON?: GeoJSON.FeatureCollection;
  lockValidFrom?: boolean;
}

const SigmetForm: React.FC<SigmetFormProps> = ({
  mode,
  isCancelSigmet = false,
  initialSigmet = null!,
  initialCancelSigmet = null!,
  showMap = true,
  productConfig = defaultSigmetConfig,
  setDialogActionsDisabled,
  product,
  getTAC,
  storedGeoJSON,
  storedEndGeoJSON,
  lockValidFrom,
}: SigmetFormProps) => {
  const { t } = useSigmetAirmetTranslation();
  const { fetchSigmetTAC } = useTACApi();
  const {
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useFormContext();
  const [hasUserChangedTime, setHasUserChangedTime] = React.useState(false);

  const firBorderAtStart = initialSigmet
    ? initialSigmet.locationIndicatorATSR
    : productConfig.active_firs[0];

  const [phenomenaList, setPhenomenaList] = React.useState<
    AviationPhenomenon[]
  >(getDefaultPhenomena(productConfig).phenomena);

  const sourcePanelId = useSelector((store: CoreAppStore) =>
    uiSelectors.getSourcePanelId(store, uiTypes.DialogTypes.PublicWarnings),
  );

  const linkedMaps: string[] = useSelector((store) =>
    genericSelectors.getLinkedMapIds(store, sourcePanelId!),
  );

  const getSrsForMapId = useSelector((store: CoreAppStore) =>
    linkedMaps ? mapSelectors.getSrs(store, linkedMaps[0]) : undefined,
  );

  // Fallback to Mercator for '', null, or undefined
  const projection = getSrsForMapId?.trim() ? getSrsForMapId : 'EPSG:3857';

  const prevProjection = usePrevious(projection);

  // Trigger update if projection changes
  useEffect(() => {
    if (prevProjection === undefined) {
      return;
    }
    if (projection !== prevProjection) {
      if (startGeoJSON?.features?.length) {
        updateStartGeoJson(startGeoJSON);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projection, prevProjection]);

  useEffect(() => {
    if (prevProjection === undefined) {
      return;
    }
    if (projection !== prevProjection) {
      if (endGeoJSON?.features?.length) {
        updateEndGeoJson(endGeoJSON);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projection, prevProjection]);

  const {
    geoJSON: startGeoJSON,
    setGeoJSON: setStartGeoJSON,
    geoJSONIntersection: startGeoJSONIntersection,
    getLayer: getStartLayer,
    drawModes: startEditModes,
    activeTool: activeStartTool,
    changeActiveTool: changeActiveStartTool,
    deactivateTool: deactivateStartTool,
    setGeoJSONIntersectionBounds: setStartGeoJSONIntersectionBounds,
    setDrawModes: setStartEditModes,
  } = useMapDrawTool({
    defaultDrawModes: getDrawTools(
      t,
      StartOrEndDrawing.start,
      getFir(productConfig, firBorderAtStart),
    ),
    defaultGeoJSON:
      !isCancelSigmet && initialSigmet?.startGeometry
        ? addFeatureProperties(initialSigmet.startGeometry, featurePropsStart)
        : undefined,
    defaultGeoJSONIntersection:
      !isCancelSigmet && initialSigmet?.startGeometryIntersect
        ? addFeatureProperties(
            initialSigmet.startGeometryIntersect,
            featurePropsIntersectionStart,
          )
        : undefined,
    defaultGeoJSONIntersectionBounds: addFeatureProperties(
      getFir(productConfig, firBorderAtStart),
      featurePropsFIR,
    ),
    projection,
  });

  const {
    geoJSON: endGeoJSON,
    setGeoJSON: setEndGeoJSON,
    geoJSONIntersection: endGeoJSONIntersection,
    getLayer: getEndLayer,
    drawModes: endEditModes,
    activeTool: activeEndTool,
    changeActiveTool: changeActiveEndTool,
    deactivateTool: deactivateEndTool,
    setGeoJSONIntersectionBounds: setEndGeoJSONIntersectionBounds,
    setDrawModes: setEndEditModes,
  } = useMapDrawTool({
    defaultDrawModes: getDrawTools(
      t,
      StartOrEndDrawing.end,
      getFir(productConfig, firBorderAtStart),
    ),
    defaultGeoJSON:
      !isCancelSigmet && initialSigmet?.endGeometry
        ? addFeatureProperties(initialSigmet.endGeometry, featurePropsEnd)
        : undefined,
    defaultGeoJSONIntersection:
      !isCancelSigmet && initialSigmet?.endGeometryIntersect
        ? addFeatureProperties(
            initialSigmet.endGeometryIntersect,
            featurePropsIntersectionEnd,
          )
        : undefined,
    defaultGeoJSONIntersectionBounds: addFeatureProperties(
      getFir(productConfig, firBorderAtStart),
      featurePropsFIR,
    ),
    defaultGeoJSONIntersectionProperties: featurePropsIntersectionEnd,
    projection,
  });

  const getHasFormErrors = (): boolean => Object.keys(errors).length > 0;

  const [tac, updateTac] = useTAC(
    t,
    initialSigmet ?? initialCancelSigmet,
    (getTAC ?? fetchSigmetTAC) as (
      product: AviationProduct,
    ) => Promise<{ data: string }>,
    getHasFormErrors,
    getOmitChangeFromConfig(productConfig),
  );

  React.useEffect(() => {
    updateTac(watch);
  }, [initialSigmet, updateTac, watch]);

  const onChangeForm = useCallback((): void => {
    updateTac(watch);
  }, [updateTac, watch]);

  const onUpdateStartgeoJSON = useCallback(
    (
      updatedStartGeoJSON: GeoJSON.FeatureCollection,
      updateStartGeoJSONIntersection: GeoJSON.FeatureCollection,
    ): void => {
      setValue('startGeometry', updatedStartGeoJSON, {
        shouldValidate: false,
        shouldDirty: isGeometryDirty(
          watch('startGeometry'),
          updatedStartGeoJSON,
        ),
      });
      setValue('startGeometryIntersect', updateStartGeoJSONIntersection, {
        shouldValidate: false,
        shouldDirty: isGeometryDirty(
          watch('startGeometryIntersect'),
          updateStartGeoJSONIntersection,
        ),
      });
      queueMicrotask(() => void trigger('startGeometry'));

      onChangeForm();
    },
    [setValue, watch, trigger, onChangeForm],
  );

  const onUpdateEndGeoJson = useCallback(
    (
      updatedEndGeoJSON: GeoJSON.FeatureCollection,
      updateEndGeoJSONIntersection: GeoJSON.FeatureCollection,
    ): void => {
      setValue('endGeometry', updatedEndGeoJSON, {
        shouldValidate: true,
        shouldDirty: isGeometryDirty(watch('endGeometry'), updatedEndGeoJSON),
      });
      setValue('endGeometryIntersect', updateEndGeoJSONIntersection, {
        shouldValidate: true,
        shouldDirty: isGeometryDirty(
          watch('endGeometryIntersect'),
          updateEndGeoJSONIntersection,
        ),
      });
      onChangeForm();
    },
    [setValue, watch, onChangeForm],
  );

  const updateStartGeoJson = useCallback(
    (geoJson: GeoJSON.FeatureCollection): void => {
      const [newStartGeoJson, newStartIntersection] = setStartGeoJSON(geoJson);
      const newGeoStart = rewindGeometry(newStartGeoJson);
      const newGeoStartIntersect = rewindGeometry(newStartIntersection!);
      onUpdateStartgeoJSON(newGeoStart, newGeoStartIntersect!);
    },
    [setStartGeoJSON, onUpdateStartgeoJSON],
  );

  const updateEndGeoJson = useCallback(
    (newGeoJSON?: GeoJSON.FeatureCollection): void => {
      const [newEndGeoJson, newEndIntersection] = setEndGeoJSON(newGeoJSON!);
      const newGeoEnd = rewindGeometry(newEndGeoJson);
      const newGeoEndIntersect = rewindGeometry(newEndIntersection!);
      onUpdateEndGeoJson(newGeoEnd, newGeoEndIntersect!);
    },
    [setEndGeoJSON, onUpdateEndGeoJson],
  );

  const { onChangeStartTool, onChangeEndTool, drawToolsDisabled } =
    useGeoJSONHandling({
      storedGeoJSON,
      storedEndGeoJSON,
      startGeoJSON,
      endGeoJSON,
      setGeoJSON: setStartGeoJSON,
      setEndGeoJSON,
      updateStartGeoJson,
      updateEndGeoJson,
      deactivateStartTool,
      deactivateEndTool,
      changeActiveStartTool,
      changeActiveEndTool,
      startEditModes,
      endEditModes,
      currentStartDrawTool: activeStartTool,
      currentEndDrawTool: activeEndTool,
    });

  const layers: OpenLayersLayerProps[] = [
    getStartLayer('geoJSONIntersectionBounds', 'geojsonlayer-fir'),
    {
      ...getEndLayer('geoJSON', 'geojsonlayer-end'),
      exitDrawModeCallback: deactivateEndTool,
      updateGeojson: updateEndGeoJson,
    },
    {
      ...getStartLayer('geoJSON', 'geojsonlayer-start'),
      exitDrawModeCallback: deactivateStartTool,
      updateGeojson: updateStartGeoJson,
    },
    getEndLayer('geoJSONIntersection', 'geojsonlayer-intersection-end'),
    getStartLayer('geoJSONIntersection', 'geojsonlayer-intersection-start'),
  ];

  const linkedFormLayers: OpenLayersLayerProps[] = [
    getStartLayer('geoJSONIntersectionBounds', 'geojsonlayer-fir'),
    getEndLayer('geoJSONIntersection', 'geojsonlayer-intersection-end'),
    getStartLayer('geoJSONIntersection', 'geojsonlayer-intersection-start'),
  ];

  useFormLayers(linkedFormLayers);

  const [isDisabled, setIsDisabled] = React.useState(mode === 'view');
  const isReadOnly = mode === 'view';
  const helperText = isReadOnly ? '' : 'Optional';

  const exitDrawMode = useCallback((): void => {
    // Let OpenLayers commit an in-progress sketch before deactivating (e.g. partial polygon).
    window.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Escape',
        code: 'Escape',
        bubbles: true,
      }),
    );

    if (activeStartTool !== '') {
      deactivateStartTool();
    }
    if (activeEndTool !== '') {
      deactivateEndTool();
    }

    queueMicrotask(() => {
      void trigger('startGeometry');
      if (!isCancelSigmet) {
        void trigger('endGeometry');
      }
    });
  }, [
    activeStartTool,
    activeEndTool,
    deactivateStartTool,
    deactivateEndTool,
    isCancelSigmet,
    trigger,
  ]);

  React.useEffect(() => {
    const isInDrawMode = activeStartTool !== '' || activeEndTool !== '';
    if (!isInDrawMode) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      exitDrawMode();
    };

    document.addEventListener('keydown', onKeyDown, true);

    return (): void => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [activeStartTool, activeEndTool, exitDrawMode]);

  React.useEffect(() => {
    const isInDrawMode = activeStartTool !== '' || activeEndTool !== '';
    setDialogActionsDisabled?.(isInDrawMode);
    if (mode === 'view') {
      setIsDisabled(true);
    } else {
      setIsDisabled(isInDrawMode);
    }
  }, [mode, activeStartTool, activeEndTool, setDialogActionsDisabled]);

  const { hasVolcanicAshes, omitChange } = useConditionalFields(productConfig);
  const { isShortTest } = useShortTestHelpers();

  const onChangeFir = (
    newPhenomenaList: AviationPhenomenon[],
    event?: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newFIRValue = event?.target.value;
    const newFirGeoJSON = addFeatureProperties(
      getFir(productConfig, newFIRValue),
      featurePropsFIR,
    );
    // delete shape
    onChangeStartTool(startEditModes.find((mode) => mode.value === 'DELETE')!);
    onChangeEndTool(endEditModes.find((mode) => mode.value === 'DELETE')!);
    // update instersection bounds
    setStartGeoJSONIntersectionBounds(newFirGeoJSON);
    setEndGeoJSONIntersectionBounds(newFirGeoJSON);
    // update tool buttons with new fir geoJSON
    setStartEditModes(updateDrawToolsWithFir(startEditModes, newFirGeoJSON));
    setEndEditModes(updateDrawToolsWithFir(endEditModes, newFirGeoJSON));
    // update available phenomena for the FIR
    setPhenomenaList(newPhenomenaList);
  };

  // Get coordinates format for VA_CLD eruption point
  const firConfig = Object.values(
    (productConfig as SigmetConfig).fir_areas,
  ).find(
    (fir) => fir.location_indicator_atsr === watch('locationIndicatorATSR'),
  );
  const degreesAndMinutes = firConfig?.va_cld_degrees_minutes === true;

  const renderForm = (): React.ReactElement => (
    <Grid container spacing={2} sx={{ ...formStyles, flexDirection: 'column' }}>
      {/* Type */}
      <Type
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        onChange={onChangeForm}
        productType="sigmet"
      />
      {/* Phenomenon */}
      {!isCancelSigmet && (
        <Phenomenon
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onChange={onChangeForm}
          phenomenaList={phenomenaList}
          productConfig={productConfig}
          productType="sigmet"
        />
      )}
      {/* Observed/Forecast */}
      {!isCancelSigmet && (
        <ObservationForecast isDisabled={isDisabled} isReadOnly={isReadOnly} />
      )}
      {/* At */}
      {!isCancelSigmet && (
        <ObservationForecastTime
          helperText={helperText}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onChange={onChangeForm}
        />
      )}
      {/* Volcanic fields */}
      {!isCancelSigmet && hasVolcanicAshes && !isShortTest() ? (
        <VolcanicFields
          degreesAndMinutes={degreesAndMinutes}
          helperText={helperText}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
        />
      ) : null}
      {/* Valid from */}
      <ValidFrom
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        lockBecauseActive={lockValidFrom}
        onChange={onChangeForm}
        onUserChange={() => setHasUserChangedTime(true)}
        productConfig={productConfig}
      />
      {/* Valid until */}
      <ValidUntil
        hasUserChangedTime={hasUserChangedTime}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        onChange={onChangeForm}
        onUserChange={() => setHasUserChangedTime(true)}
        productConfig={productConfig}
      />
      {/* Where */}
      <SelectFIR
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        onChange={(event, newPhenomenaList) => {
          onChangeFir(newPhenomenaList ?? [], event);
        }}
        productConfig={productConfig}
      />
      {/* Draw */}
      <StartGeometry
        activeTool={activeStartTool}
        geoJSON={startGeoJSON}
        geoJSONIntersection={startGeoJSONIntersection}
        isDisabled={drawToolsDisabled}
        isReadOnly={isReadOnly}
        onChangeTool={onChangeStartTool}
        productConfig={productConfig}
        tools={startEditModes}
      />
      {/* Levels */}
      {!isCancelSigmet && (
        <Levels
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onChange={onChangeForm}
          productConfig={productConfig}
          productType="sigmet"
        />
      )}
      {/* Progress */}
      {!isCancelSigmet && (
        <Progress
          activeTool={activeEndTool}
          drawToolsDisabled={drawToolsDisabled}
          geoJSON={endGeoJSON}
          geoJSONIntersection={endGeoJSONIntersection!}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onChange={onChangeForm}
          onChangeTool={onChangeEndTool}
          productConfig={productConfig}
          productType="sigmet"
          tools={endEditModes}
        />
      )}
      {/* Change */}
      {!isCancelSigmet && !omitChange && (
        <Change isDisabled={isDisabled} isReadOnly={isReadOnly} />
      )}
      {/* Volcanic ash cloud moving to: */}
      {isCancelSigmet && watch('vaSigmetMoveToFIR') ? (
        <ProductFormFieldLayout>
          <Typography
            data-testid="vaSigmetMoveToFIR"
            sx={{ ...styles.body, marginBottom: '-4px' }}
            variant="body2"
          >
            {t('vulcanic-cloud-moving-text')}:{' '}
            <b> {watch('vaSigmetMoveToFIR')}</b>
          </Typography>
        </ProductFormFieldLayout>
      ) : null}
      {/* Issued at */}
      <IssuedAt
        date={getProductIssueDate(t, initialSigmet, initialCancelSigmet)}
        isReadOnly={isReadOnly}
      />
      {/* TAC */}
      <Tac product={product!} tac={tac}>
        {/* Hidden sequence number field needed for the TAC generation */}
        <ReactHookFormHiddenInput defaultValue="-1" name="sequence" />

        {
          /* Add hidden fields for TAC generation in case of cancel sigmet */
          isCancelSigmet ? (
            <>
              <ReactHookFormHiddenInput name="cancelsSigmetSequenceId" />
              <ReactHookFormHiddenInput name="validDateEndOfSigmetToCancel" />
              <ReactHookFormHiddenInput name="validDateStartOfSigmetToCancel" />
            </>
          ) : null
        }
      </Tac>
    </Grid>
  );
  if (!showMap) {
    return renderForm();
  }

  return (
    <form
      onChange={onChangeForm}
      onSubmit={handleSubmit(() => null)}
      style={{ width: '100%' }}
    >
      <ProductFormLayout geoJSONLayers={layers} productConfig={productConfig}>
        {renderForm()}
      </ProductFormLayout>
    </form>
  );
};

export default SigmetForm;
